import { clearUserSessionStores } from "@/lib/store-utils"
import { identifyUser } from "@/lib/tracking/analytics"
import { authStore } from "@/store/auth"

import { hijackService } from "../services/hijack-service"
import { useRecentUsersStore } from "../store/recent-users-store"

export async function startImpersonation(targetUserId: string): Promise<void> {
  const currentState = authStore.getState()

  // Save current admin session
  const originalAdminToken = currentState.token
  const originalAdminRefreshToken = currentState.refreshToken
  const originalAdminUser = currentState.user

  // Call hijack API
  const response = await hijackService.hijackUser(targetUserId)
  const { access_token, user, impersonator_id } = response.data

  useRecentUsersStore.getState().registerAccess({
    id: user.id,
    name: user.name,
    email: user.email,
    lastAccessType: "hijack",
  })

  // Clear session stores before switching context
  clearUserSessionStores()

  // Update store with impersonated user data
  authStore.setState({
    token: access_token,
    refreshToken: null, // No refresh token for impersonated session
    tokenType: "bearer",
    user,
    isAuthenticated: true,
    isImpersonating: true,
    impersonatorId: impersonator_id,
    originalAdminToken,
    originalAdminRefreshToken,
    originalAdminUser,
  })

  // Re-identify in PostHog/Mixpanel so feature flags evaluate for the impersonated user
  identifyUser(user.id, {
    email: user.email,
    name: user.name,
    organization_id: user.organization_id,
    is_impersonated: true,
    impersonator_id: impersonator_id,
  })
}

export function stopImpersonation(): void {
  const currentState = authStore.getState()

  const { originalAdminToken, originalAdminRefreshToken, originalAdminUser } = currentState

  // Clear session stores before switching back
  clearUserSessionStores()

  // Restore admin session
  authStore.setState({
    token: originalAdminToken,
    refreshToken: originalAdminRefreshToken,
    tokenType: "bearer",
    user: originalAdminUser,
    isAuthenticated: true,
    isImpersonating: false,
    impersonatorId: null,
    originalAdminToken: null,
    originalAdminRefreshToken: null,
    originalAdminUser: null,
  })

  // Re-identify in PostHog/Mixpanel as the admin user
  if (originalAdminUser) {
    identifyUser(originalAdminUser.id, {
      email: originalAdminUser.email,
      name: originalAdminUser.name,
      organization_id: originalAdminUser.organization_id,
      is_impersonated: false,
    })
  }
}
